import { Movie, MovieFilters, PaginatedMoviesResponse } from '../types/movie';

const API_BASE_URL = '/api';

// Simple in-memory cache for movies
let moviesCache: { data: PaginatedMoviesResponse; timestamp: number } | null = null;
const CACHE_DURATION = 30 * 1000; // 30 seconds in milliseconds (reduced for better responsiveness)

// Function to clear the cache
export const clearMoviesCache = () => {
  moviesCache = null;
  console.log('Movies cache cleared');
};

// Function to invalidate cache (more explicit than setting to null)
export const invalidateMoviesCache = () => {
  moviesCache = null;
  console.log('Movies cache invalidated');
};

// Make cache functions available globally for debugging
if (typeof window !== 'undefined') {
  (window as any).clearMoviesCache = clearMoviesCache;
  (window as any).invalidateMoviesCache = invalidateMoviesCache;
}

export interface ApiResponse<T> {
  data?: T;
  error?: string;
}

// Get movies with optional filters and pagination
export const getMovies = async (filters?: MovieFilters, forceRefresh: boolean = false): Promise<PaginatedMoviesResponse> => {
  try {
    // Check cache first (only for unfiltered requests with same pagination)
    if (!filters || (Object.keys(filters).length === 0 || (filters.page === 1 && filters.limit === 100)) && !forceRefresh) {
      if (moviesCache && (Date.now() - moviesCache.timestamp) < CACHE_DURATION) {
        console.log(`Using cached movies data (${moviesCache.data.movies.length} movies)`);
        return moviesCache.data;
      } else if (moviesCache) {
        console.log('Cache expired, fetching fresh data');
      } else {
        console.log('No cache available, fetching fresh data');
      }
    } else {
      if (forceRefresh) {
        console.log('Force refresh requested, bypassing cache');
      } else {
        console.log('Filters/pagination applied, bypassing cache');
      }
    }
    
    const params = new URLSearchParams();
    
    // Set pagination parameters
    const page = filters?.page || 1;
    const limit = filters?.limit || 100;
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    
    if (filters) {
      if (filters.search) params.append('search', filters.search);
      if (filters.scoreAbove7) params.append('scoreAbove7', filters.scoreAbove7.toString());
      if (filters.genres && filters.genres.length > 0) params.append('genres', filters.genres.join(','));
      if (filters.director) params.append('director', filters.director);
      if (filters.gf) params.append('gf', filters.gf.toString());
      if (filters.sortBy) params.append('sortBy', filters.sortBy);
      if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);
    }
    
    const url = `${API_BASE_URL}/movies${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await fetch(url);
    
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    
    // Helper function to safely parse genres
    const parseGenres = (genresStr: string): string[] => {
      if (typeof genresStr !== 'string') return [];
      
      try {
        // Try to parse as JSON first
        return JSON.parse(genresStr);
      } catch (e) {
        // If JSON parsing fails, try to extract genres from string format like "[Comedy,Drama]"
        const match = genresStr.match(/\[(.*)\]/);
        if (match) {
          return match[1].split(',').map(g => g.trim()).filter(Boolean);
        }
        // Fallback: split by comma and clean up
        return genresStr.split(',').map(g => g.trim().replace(/[\[\]"]/g, '')).filter(Boolean);
      }
    };

    // Helper function to convert date serial number to date string
    const parseDate = (dateValue: any): string | null => {
      if (!dateValue) return null;
      
      // If it's already a string, return it
      if (typeof dateValue === 'string') {
        return dateValue;
      }
      
      // If it's a number (date serial), convert it
      if (typeof dateValue === 'number') {
        // For now, just return a test string to see if the function is being called
        return `2025-08-27 (from ${dateValue})`;
      }
      
      return null;
    };
    
    // Process the response data
    const processedMovies = data.movies ? data.movies.map((movie: any) => ({
      ...movie,
      title: movie.title || '',
      director: movie.director || '',
      genres: parseGenres(movie.genres),
      gf: Boolean(movie.gf),
      lastSeen: parseDate(movie.last_seen),
    })) : [];
    
    const paginatedResponse: PaginatedMoviesResponse = {
      movies: processedMovies,
      totalCount: data.totalCount || processedMovies.length,
      currentPage: data.currentPage || page,
      totalPages: data.totalPages || Math.ceil((data.totalCount || processedMovies.length) / limit),
      limit: data.limit || limit
    };
    
    // Cache the result (only for first page, no filters)
    if (!filters || (Object.keys(filters).length === 0 || (filters.page === 1 && filters.limit === 100))) {
      moviesCache = {
        data: paginatedResponse,
        timestamp: Date.now()
      };
      console.log(`Cached ${processedMovies.length} movies (page ${paginatedResponse.currentPage})`);
    }
    
    return paginatedResponse;
  } catch (error) {
    console.error('Error fetching movies:', error);
    throw error;
  }
};

// Add a new movie
export const addMovie = async (movie: Omit<Movie, 'id'>): Promise<{ id: number; message: string }> => {
  try {
    // Clear cache before making the request to ensure fresh data
    invalidateMoviesCache();
    
    const response = await fetch(`${API_BASE_URL}/movies`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(movie),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    
    // Invalidate cache again after successful addition
    invalidateMoviesCache();
    
    return result;
  } catch (error) {
    console.error('Error adding movie:', error);
    throw error;
  }
};

// Delete a movie
export const deleteMovie = async (id: number): Promise<{ message: string }> => {
  try {
    console.log(`Attempting to delete movie with ID: ${id}`);
    const response = await fetch(`${API_BASE_URL}/movies/${id}`, {
      method: 'DELETE',
    });
    
    console.log(`Delete response status: ${response.status}`);
    
    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorData.message || errorMessage;
      } catch (parseError) {
        console.error('Failed to parse error response:', parseError);
        // Try to get the response text for debugging
        try {
          const responseText = await response.text();
          console.error('Response text:', responseText);
        } catch (textError) {
          console.error('Failed to get response text:', textError);
        }
      }
      throw new Error(errorMessage);
    }
    
    // Invalidate cache after successful deletion
    invalidateMoviesCache();
    
    const result = await response.json();
    console.log('Delete successful:', result);
    return result;
  } catch (error) {
    console.error('Error deleting movie:', error);
    throw error;
  }
};

// Update movie score
export const updateMovieScore = async (id: number, score: number): Promise<{ message: string }> => {
  try {
    const response = await fetch(`${API_BASE_URL}/movies/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ score }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }
    
    // Invalidate cache after successful update
    invalidateMoviesCache();
    
    return await response.json();
  } catch (error) {
    console.error('Error updating movie score:', error);
    throw error;
  }
};

// Get all movies by making multiple paginated requests
export const getAllMovies = async (): Promise<Movie[]> => {
  try {
    const allMovies: Movie[] = [];
    let currentPage = 1;
    let hasMore = true;
    const limit = 1000; // Max per page

    while (hasMore) {
      const response = await getMovies({
        search: '',
        scoreAbove7: false,
        genres: [],
        director: '',
        gf: false,
        sortBy: 'lastSeen',
        sortOrder: 'desc',
        page: currentPage,
        limit: limit
      }, false);

      allMovies.push(...response.movies);

      if (response.movies.length < limit || currentPage >= response.totalPages) {
        hasMore = false;
      } else {
        currentPage++;
      }
    }

    return allMovies;
  } catch (error) {
    console.error('Error getting all movies:', error);
    throw error;
  }
};

// Get movie statistics from dedicated stats endpoint
export const getMovieStats = async (): Promise<{
  totalMovies: number;
  averageScore: number;
  moviesWithGF: number;
  moviesAbove7: number;
  topGenres: { genre: string; count: number }[];
  topDirectors: { director: string; count: number }[];
}> => {
  try {
    const response = await fetch(`${API_BASE_URL}/movies/stats`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error getting movie stats:', error);
    throw error;
  }
};
